// Loads DAP appointment context and activates the DSS workflow for eligible store actions.
import React from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  getMenuAndStores,
  getStoreActionCoworkerId,
  getStoreActionDapAppointment,
  StoreActionCoworkerRoleResponse,
  StoreActionDapAppointmentRequest,
  StoreActionDapAppointmentResponse,
} from '../../../api/user';
import {
  hasSuccessfulStoreActionStatus,
  StoreAction,
} from './storeActionDomain';

export type StoreActionDapRequest = StoreActionDapAppointmentRequest;

export type StoreActionDapAppointment = Readonly<Record<string, unknown>> & {
  readonly appointmentId: string;
};

const getDapRequest = (
  item: StoreAction,
  storeNumber: string
): StoreActionDapRequest | null => {
  const segments = item.redirectUrl.split('/');
  const date = segments[2];
  const appointmentId = segments[3];

  if (!date || !appointmentId) {
    return null;
  }

  return {
    appointmentId,
    appointmentTypes: ['D', 'CO', 'CI'],
    endDate: date,
    startDate: date,
    storeNumber,
  };
};

export const useStoreActionRole = (hostRole: string, coworkerId: string) =>
  useQuery<StoreActionCoworkerRoleResponse | null, Error, string | null>({
    enabled: !hostRole,
    queryFn: async () => {
      const resolvedCoworkerId =
        coworkerId || (await getStoreActionCoworkerId());

      if (!resolvedCoworkerId) {
        return null;
      }
      const response: unknown = await getMenuAndStores(resolvedCoworkerId);
      if (!hasSuccessfulStoreActionStatus(response)) {
        throw new Error('Invalid coworker role response');
      }
      return response as StoreActionCoworkerRoleResponse;
    },
    queryKey: ['store-actions', 'role', coworkerId || 'current'],
    select: (response) => response?.data?.coworkerProfile?.role || null,
    staleTime: Infinity,
    useErrorBoundary: true,
  });

export const useStoreActionDapActivation = (
  onAppointment: (appointment: StoreActionDapAppointment) => void
): ((item: StoreAction, storeNumber: string) => void) => {
  const [request, setRequest] = React.useState<StoreActionDapRequest | null>(
    null
  );
  const query = useQuery<StoreActionDapAppointment, Error>({
    enabled: request !== null,
    queryFn: async () => {
      if (!request) {
        throw new Error('Missing DAP appointment request');
      }
      const response: unknown = await getStoreActionDapAppointment(request);
      if (!hasSuccessfulStoreActionStatus(response)) {
        throw new Error('Invalid DAP appointment response');
      }
      return (response as StoreActionDapAppointmentResponse)
        .data as StoreActionDapAppointment;
    },
    queryKey: ['store-actions', 'dap-appointment', request],
    useErrorBoundary: true,
  });

  React.useEffect(() => {
    if (query.data !== undefined) {
      onAppointment(query.data);
    }
  }, [onAppointment, query.data]);

  return (item, storeNumber): void => {
    const nextRequest = getDapRequest(item, storeNumber);

    if (nextRequest) {
      setRequest(nextRequest);
    }
  };
};
